import { Close, Search } from "@mui/icons-material";
import { Autocomplete, styled, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import useDebounce from "../../hooks/use-debounce";
import { IListDto } from "../../interface/dto/list.dto";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { routeMapping } from "../../route.config";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";
import { listSearchAction } from "./redux/list-search.actions";

const ListSearchBar = ({ className }: IWithClassName) => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState<string | null>(null);

    const debouncedValue = useDebounce(searchText);
    const dispatch = useAppDispatch();

    const { isLoading, lists = [] } = useAppSelector(
        ({ listSearch }) => listSearch,
    );

    useEffect(() => {
        dispatch(listSearchAction(debouncedValue ?? ""));
    }, [debouncedValue]);

    const enterKeyDownHandler = () => {
        const filteredList = lists.find(
            ({ name, hasAccess }) =>
                hasAccess && name?.toLowerCase() === searchText?.toLowerCase(),
        );

        changeHandler(filteredList ?? null);
    };

    const changeHandler = (value: string | null | IListDto) => {
        const { path } = routeMapping.searchList;

        if (typeof value === "object" && value?.listId) {
            void navigate(path.replace(":listId", value?.listId.toString()));
        }
    };

    return (
        <div className={className}>
            <Autocomplete
                freeSolo
                onChange={(event, value) => changeHandler(value)}
                options={lists}
                getOptionLabel={(list) =>
                    typeof list === "object" ? list.name : list
                }
                filterOptions={(options) => options}
                loading={isLoading}
                getOptionDisabled={(list) => !list.hasAccess}
                clearIcon={<Close className="close-icon" />}
                componentsProps={{
                    clearIndicator: {
                        onClick: () => setSearchText(""),
                    },
                }}
                value={searchText}
                renderInput={(params) => (
                    <>
                        <TextField
                            {...params}
                            value={searchText}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                            variant="standard"
                            onKeyDown={(event) =>
                                event.code === "Enter" && enterKeyDownHandler()
                            }
                        />
                        <Search className="search-icon" />
                    </>
                )}
            />
        </div>
    );
};

export default styled(ListSearchBar)(({ theme }: IWithThemeProps) => ({
    marginTop: theme?.spacing(4),
    ".MuiAutocomplete-root": {
        display: "flex",
        width: 300,
        float: "right",

        "& .MuiInput-root": {
            "& .MuiInput-input": {
                padding: "4px 25px 4px 0px",
            },
        },

        "& svg": {
            color: theme?.palette.search.iconColor,
            position: "relative",
        },

        "& .search-icon": {
            transform: "scale(1.2)",
            right: 20,
            bottom: -4.8,
        },

        "& .close-icon": {
            transform: "scale(1.5)",
        },
    },
}));
